import React from 'react';
import { Query, Mutation } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';
import PropTypes from 'prop-types';

import Create, { Mention } from './create/create';

import CommentsList from './comments-list/comments-list';

import query, { CommentsQuery, CommentsQueryVariables } from './comments.graphql';
import createCommentMutation, {
    CreateCommentMutation,
    CreateCommentMutationVariables,
} from './comment/mutations/create-comment.graphql';

interface Props {
    variables?: CommentsQueryVariables;
    commentId?: string;
    onAppear?: (ref: HTMLLIElement | null, id: string) => void;
    onMount?: () => void;
    scrollOnNew?: boolean;
    commentCount?: number;
}

class Comments extends React.PureComponent<Props> {
    static contextTypes = {
        scrollView: PropTypes.object,
    };

    static defaultProps = {
        scrollOnNew: false,
    };

    newId?: string | null;
    mention?: Mention;

    setMention = (mention: Mention) => {
        this.mention = mention;
    };

    render() {
        const { variables, commentId, commentCount, onAppear, onMount } = this.props;

        return (
            <React.Fragment>

                <Query<CommentsQuery, CommentsQueryVariables>
                    query={query}
                    variables={{ ...variables, orderBy: 'CREATED_TIME', first: 15 }}
                    notifyOnNetworkStatusChange
                    fetchPolicy='cache-first'
                >
                    {({ data, fetchMore, loading }) =>
                        data && data.comments ? (
                            <CommentsList
                                loading={loading}
                                fetchMore={() => {
                                    const endCursor = data && data.comments && data.comments.pageInfo.endCursor;
                                    return fetchMore({
                                        variables: {
                                            after: endCursor,
                                            first: 15,
                                        },
                                        updateQuery: (
                                            previousResult = {} as CommentsQuery,
                                            { fetchMoreResult = {} as CommentsQuery }
                                        ) => {
                                            return {
                                                ...previousResult,
                                                comments: mapAfter(previousResult.comments, fetchMoreResult.comments),
                                            };
                                        },
                                    });
                                }}
                                {...data.comments}
                                onAppear={(ref, appeardCommentId) => {
                                    if (ref && appeardCommentId === commentId) {
                                        this.context.scrollView.scrollToRef({ ref, offset: -10 });
                                    }

                                    if (this.props.scrollOnNew && ref && appeardCommentId === this.newId) {
                                        this.newId = null;
                                        this.context.scrollView.scrollToRef({ ref, offset: -10 });
                                    }

                                    if (onAppear) {
                                        onAppear(ref, appeardCommentId);
                                    }
                                }}
                                onComment={({ user }) => {
                                    if (user && user.username && this.mention) {
                                        this.mention(user.username);
                                    }
                                }}
                                onMount={onMount}
                                showMoreButton={data.comments.pageInfo.hasNextPage}
                            />
                        ) : null
                    }
                </Query>
                <Mutation<CreateCommentMutation, CreateCommentMutationVariables>
                    mutation={createCommentMutation}
                    update={(cache, { data: result }) => {
                        const comment = result && result.createComment && result.createComment.comment;

                        if (comment) {
                            this.newId = comment.id;
                            const cached = cache.readQuery<CommentsQuery, CommentsQueryVariables>({
                                query,
                                variables: {
                                    ...variables,
                                    orderBy: 'CREATED_TIME',
                                    first: 15,
                                },
                            });

                            if (cached) {
                                cache.writeQuery({
                                    query,
                                    variables: {
                                        ...variables,
                                        orderBy: 'CREATED_TIME',
                                        first: 15,
                                    },
                                    data: {
                                        ...cached,
                                        comments: {
                                            ...cached.comments,
                                            edges: [
                                                ...cached.comments!.edges,
                                                {
                                                    cursor: comment.id,
                                                    node: {
                                                        ...comment,
                                                    },
                                                    __typename: 'CommentNodeEdge',
                                                },
                                            ],
                                            pageInfo: {
                                                ...cached.comments!.pageInfo,
                                                endCursor: comment.id,
                                            },
                                        },
                                    },
                                });
                            }
                        }
                    }}
                >
                    {create =>
                        variables ? (
                            <Create
                                submit={({ comment, image }) =>
                                    create({
                                        variables: {
                                            id: variables.parentId,
                                            comment,
                                            photoFiles: image ? [image.id] : undefined,
                                        },
                                    })
                                }
                                autoFocusInput={commentCount === 0}
                                mention={this.setMention}
                            />
                        ) : null
                    }
                </Mutation>
            </React.Fragment>
        );
    }
}

export default Comments;
